import { useQuery } from "@apollo/client";
import { Controller, useFormContext } from "react-hook-form";
import { FormGroup } from "@cofano/cck";
import z from "zod";
import { CharterType, StatusType } from "__generated__/enums";
import { DeclarationCountDocument } from "__generated__/view/dashboard/graphql/dashboard.queries.generated";
import { SelectInvoiceGroupTypesDocument } from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.queries.generated";
import { GetAllStatusesDocument } from "__generated__/view/statuses/graphql/queries.generated";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";
import InvoiceGroupTypeSelect from "view/invoicegrouptypes/components/selects/InvoiceGroupTypeSelect";
import StatusSelect from "view/statuses/components/selects/StatusSelect";
import { createWidgetSource } from "view/dashboard/widgets/sources/types";
import { intl } from "LocaleWrapper";

export default createWidgetSource({
	valueType: "number",
	label: intl.formatMessage({ id: "DECLARATIONS" }),
	configSchema: z.object({
		statusIds: z.array(z.number()).default([]),
		charterTypes: z.array(z.enum(CharterType)).default([]),
		invoiceGroupTypeIds: z.array(z.number()).default([]),
	}),

	form: ({ prefix }) => {
		const { control, watch } = useFormContext();

		const charterTypes = (watch(`${prefix}.charterTypes`) ?? []) as Array<CharterType>;

		const { data: statusData } = useQuery(GetAllStatusesDocument, {
			variables: { search: { types: [ StatusType.Declaration ] } },
		});
		const statuses = statusData?.allStatuses ?? [];

		const { data: invoiceGroupTypeData } = useQuery(SelectInvoiceGroupTypesDocument, {
			variables: { search: { excludeAdvancePayment: false } },
		});
		const invoiceGroupTypes = invoiceGroupTypeData?.invoiceGroupTypes ?? [];

		return <>
			<FormGroup label={intl.formatMessage({ id: "CHARTER_TYPE" })} labelPlacement="left">
				<Controller
					name={`${prefix}.charterTypes`}
					control={control}
					render={({ field }) => (
						<CharterTypeSelect<true>
							multiple
							value={(field.value as Array<CharterType>) ?? []}
							onChange={selected => field.onChange(selected ?? [])} />
					)} />
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "STATUS" })} labelPlacement="left">
				<Controller
					name={`${prefix}.statusIds`}
					control={control}
					render={({ field }) => {
						const ids = (field.value as Array<number>) ?? [];

						return <StatusSelect
							multiple
							statuses={statuses}
							value={statuses.filter(status => ids.includes(status.id))}
							onChange={selected => field.onChange((selected ?? []).map(status => status.id))} />;
					}} />
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "INVOICE_GROUP_TYPE" })} labelPlacement="left">
				<Controller
					name={`${prefix}.invoiceGroupTypeIds`}
					control={control}
					render={({ field }) => {
						const ids = (field.value as Array<number>) ?? [];

						return <InvoiceGroupTypeSelect<true>
							multiple
							charterType={charterTypes.length === 1 ? charterTypes[0] : null}
							value={invoiceGroupTypes.filter(type => ids.includes(type.id))}
							onChange={selected => field.onChange((selected ?? []).map(type => type.id))} />;
					}} />
			</FormGroup>
		</>;
	},

	useValue: config => {
		const { data, loading, error } = useQuery(DeclarationCountDocument, {
			variables: {
				search: {
					statusIds: config.statusIds.length > 0 ? config.statusIds : undefined,
					charterTypes: config.charterTypes.length > 0 ? config.charterTypes : undefined,
					invoiceGroupTypeIds: config.invoiceGroupTypeIds.length > 0 ? config.invoiceGroupTypeIds : undefined,
				},
				pagination: { page: 0, size: 1 },
			},
		});

		return { value: data?.searchDeclarations.totalElements, loading, error };
	},
});
